import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Table, Alert, Modal, Form, InputGroup } from 'react-bootstrap';
import { api, getBaseUrl } from '../../../shared/api';
import Select from '../../../components/form/AppSelect';
import '../../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next'; // Import translation hook
import { usePermissions } from '../../../shared/permissions';
import { useDateFormat } from '../../../shared/DateFormatContext';
import { useConfirm } from '../../../shared/ConfirmContext';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CenteredLoader } from '../../../components/LoadingSpinner';
import PageSection from '../../../components/layout/PageSection';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

// Debounce function to limit API calls during typing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const ProjectFiles = ({ projectId }) => {
  const { t } = useTranslation(); // Add translation hook
  const { formatDateTime } = useDateFormat();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState([]);
  const [allFiles, setAllFiles] = useState([]);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [selectedFileType, setSelectedFileType] = useState(null);

  const { isLoading: loading, data: queryData } = useQuery({
    queryKey: ['project-files', projectId],
    queryFn: async () => {
      const response = await api.get(`projects/api/project-files/?project_id=${projectId}`);
      setFiles(response);
      setAllFiles(response);
      return response;
    },
  });

  useEffect(() => {
    if (!queryData) return;
    setAllFiles(queryData || []);
    setFiles(queryData || []);
  }, [queryData]);

  const fetchFiles = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['project-files', projectId] });
  }, [queryClient, projectId]);

  // ── realtime updates ─────────────────────────────────────────────────────
  const _filesDebounce = useRef(null);
  useRealtimeEvents(useCallback((e) => {
    if (e.model !== 'projects.projectfile') return;
    clearTimeout(_filesDebounce.current);
    _filesDebounce.current = setTimeout(() => fetchFiles(), 400);
  }, [fetchFiles])); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter files based on search query and selected file type
  useEffect(() => {
    if (allFiles.length === 0) return;

    let filteredFiles = [...allFiles];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredFiles = filteredFiles.filter(file =>
        file.name.toLowerCase().includes(query) ||
        (file.description && file.description.toLowerCase().includes(query))
      );
    }

    // Filter by file type
    if (selectedFileType) {
      filteredFiles = filteredFiles.filter(file => {
        const fileExtension = file.name.split('.').pop().toLowerCase();
        return fileExtension === selectedFileType.value;
      });
    }

    setFiles(filteredFiles);
  }, [searchQuery, selectedFileType, allFiles]);

  // Generate file type options based on available files
  useEffect(() => {
    if (allFiles.length === 0) return;

    const fileTypes = new Set();
    allFiles.forEach(file => {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (fileExtension) {
        fileTypes.add(fileExtension);
      }
    });

    const options = Array.from(fileTypes).map(type => ({
      value: type,
      label: type.toUpperCase()
    }));

    setFileTypeOptions(options);
  }, [allFiles]);

  const fetchFiles_OLD = async () => {
    // removed - now handled by useQuery above
  };

  const searchFiles = useCallback(
    debounce(async (query) => {
      if (!projectId) return;
      try {
        const response = await api.get(
          `projects/api/project-files/search/?project_id=${projectId}&query=${encodeURIComponent(query)}`
        );
        setFiles(response);
      } catch (err) {
        console.error('Error searching files:', err);
      }
    }, 500),
    [projectId]
  );

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.length >= 3) {
      searchFiles(query);
    } else if (query.length === 0) {
      setFiles(allFiles);
    }
  };

  const handleFileTypeChange = (selectedOption) => {
    setSelectedFileType(selectedOption);
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      setError(t('projects.files.selectFileWarning'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Create FormData for file upload
    const formData = new FormData();
    formData.append('project', projectId);
    formData.append('file', selectedFile);
    formData.append('name', selectedFile.name);
    formData.append('description', description);

    try {
      // Option 1: Use our updated api.post method which now handles FormData correctly
      await api.post('projects/api/project-files/', formData);

      // Option 2: For progress tracking (if uploadWithProgress is implemented in your API)
      /*
      await api.uploadWithProgress(
        'projects/api/project-files/',
        formData,
        (percent) => setUploadProgress(percent)
      );
      */

      // Reset form
      setSelectedFile(null);
      setDescription('');
      setShowModal(false);
      setError(null);

      fetchFiles(); // Refresh file list
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(t('projects.files.errorUpload'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (await confirm(t('projects.files.confirmDelete'))) { // Translate confirmation message
      try {
        await api.delete(`projects/api/project-files/${fileId}/`);
        fetchFiles();
      } catch (err) {
        console.error('Error deleting file:', err);
        setError(t('projects.files.errorDelete'));
      }
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      const baseUrl = getBaseUrl();
      const url = `${baseUrl}/projects/api/project-files/${fileId}/download/`;

      // Use fetch directly for binary data
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      // Get blob data
      const blob = await response.blob();

      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      setError(t('projects.files.errorDownload'));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) return <CenteredLoader />;

  return (
    <>
      <PageSection
        title={t('projects.files.title')}
        description={t('projects.files.sectionDescription')}
        bodyClassName="p-0"
        actions={canManage ? (
          <Button variant="primary" size="sm" onClick={() => setShowModal(true)}>
            {t('projects.files.uploadFile')}
          </Button>
        ) : null}
      >
        <div className="px-3 pt-3">
          <div className="mb-3 row">
            <div className="col-md-6 mb-2 mb-md-0">
              <InputGroup>
                <Form.Control
                  placeholder={t('projects.files.searchPlaceholder')}
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </div>
            <div className="col-md-6">
              <Select
                value={selectedFileType}
                onChange={handleFileTypeChange}
                options={fileTypeOptions}
                placeholder={t('projects.files.filterByType')}
                isClearable
                isSearchable
                theme={selectTheme}
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </div>

          {error && <Alert variant="danger" className="app-inline-alert">{error}</Alert>}

          {files.length === 0 ? <Alert variant="info" className="app-inline-alert mb-3">{t('projects.files.noFiles')}</Alert> : null}
        </div>

        {files.length > 0 ? (
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('projects.files.fileName')}</th>
                  <th>{t('projects.files.description')}</th>
                  <th>{t('projects.files.size')}</th>
                  <th>{t('projects.files.uploadedBy')}</th>
                  <th>{t('projects.files.uploadDate')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {files.map(file => (
                  <tr key={file.id}>
                    <td>{file.name}</td>
                    <td>{file.description || '-'}</td>
                    <td>{formatFileSize(file.file_size)}</td>
                    <td>{file.uploaded_by_name}</td>
                    <td>{formatDateTime(file.uploaded_at)}</td>
                    <td>
                      <div className="btn-group">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDownload(file.id, file.name)}
                          title={t('projects.files.download')}
                        >
                          <i className="ti ti-download"></i>
                        </Button>
                        {canManage && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(file.id)}
                            title={t('common.delete')}
                          >
                            <i className="ti ti-trash"></i>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </PageSection>

      {/* Upload File Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('projects.files.uploadFile')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('projects.files.selectFile')}</Form.Label>
              <Form.Control
                type="file"
                onChange={handleFileChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('projects.files.descriptionOptional')}</Form.Label>
              <Form.Control
                as="textarea"
                value={description}
                onChange={handleDescriptionChange}
                rows={3}
              />
            </Form.Group>

            {uploading && (
              <div className="mb-3">
                <Form.Label>{t('projects.files.uploadProgress', { percent: uploadProgress })}</Form.Label>
                <div className="progress">
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" type="submit" disabled={uploading || !selectedFile}>
                {uploading ? t('projects.files.uploading') : t('projects.files.upload')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ProjectFiles;
